import { FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { DateOnly, maxDateValidator, minDateValidator, PracticeInfo, setRequired, SURGEON, US_CODE, UserProfileCountry } from '../core';


const SPECIAL_CHARS_PATTERN: RegExp = /^[^<>%#&?,]+$/;
const SPECIAL_CHARS_TEXT = '<>%#&?,';
const SPECIAL_CHARS_LITE_PATTERN: RegExp = /^[^<>%#?]+$/;
const SPECIAL_CHARS_LITE_TEXT = '<>%#?';
const longName_maxLength = 200;
const name_maxLength = 50;


export class ProfileControl implements IProfileControl {
	email = new FormControl<string | null>(null);
	firstName = new FormControl<string | null>(null);
	lastName = new FormControl<string | null>(null);
	country = new FormControl<UserProfileCountry | null>(null);
	roleInfo = new FormControl<string | null>(null);
	practice = new FormControl<PracticeInfo | null>(null);
	dateOfBirth = new FormControl<DateOnly | null>(null, [
		Validators.required,
		maxDateValidator(new DateOnly(new DateOnly().setFullYear(new DateOnly().getFullYear() - 18) + 86400000)),
		minDateValidator(new DateOnly(Date.UTC(1899, 12, 1)))
	]);
	dateOfBirthFormat = new FormControl<string | null>(null);
	distributor = new FormControl<string | null>(null, [Validators.maxLength(longName_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	hospital = new FormControl<string | null>(null, [Validators.maxLength(longName_maxLength), Validators.pattern(SPECIAL_CHARS_LITE_PATTERN)]);
	company = new FormControl<string | null>(null, [Validators.maxLength(longName_maxLength), Validators.pattern(SPECIAL_CHARS_LITE_PATTERN)]);
	address = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(longName_maxLength), Validators.pattern(SPECIAL_CHARS_LITE_PATTERN)]);
	city = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(name_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	stateProvince = new FormControl<string | null>(null, [Validators.maxLength(longName_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	usStateProvince = new FormControl<string | null>(null);
	postalCode = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(name_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	officePhone = new FormControl<string | null>(null, [Validators.required, Validators.maxLength(name_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
	mobilePhone = new FormControl<string | null>(null, [Validators.maxLength(name_maxLength), Validators.pattern(SPECIAL_CHARS_PATTERN)]);
}

export function setProfileValidators(form: FormGroup<ProfileControl>): void {
	combineLatest([form.controls.roleInfo.valueChanges, form.controls.country.valueChanges]).subscribe(([role, country]) => {
		setRequired(form.controls.distributor, role === SURGEON);
		setRequired(form.controls.hospital, role === SURGEON);
		setRequired(form.controls.company, role !== SURGEON);
		setRequired(form.controls.stateProvince, country?.code !== US_CODE);
		setRequired(form.controls.usStateProvince, country?.code === US_CODE);
	});
}

export const profileErrorMsgs: { [key in keyof ProfileForm]: { [y: string]: string[] } | null } = {
	email: null,
	firstName: null,
	lastName: null,
	country: null,
	roleInfo: null,
	practice: null,
	dateOfBirth: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_DATE_OF_BIRTH'],
		'maxDate': ['VALIDATION_MESSAGE_MAXDATE_DATE_OF_BIRTH'],
		'minDate': ['VALIDATION_MESSAGE_INVALID_DATE_OF_BIRTH']
	},
	dateOfBirthFormat: null,
	distributor: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_DISTRIBUTOR'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_DISTRIBUTOR', longName_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_DISTRIBUTOR', SPECIAL_CHARS_TEXT]
	},
	hospital: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_HOSPITAL'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_HOSPITAL', longName_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_HOSPITAL', SPECIAL_CHARS_LITE_TEXT]
	},
	company: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_COMPANY'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_COMPANY', longName_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_COMPANY', SPECIAL_CHARS_LITE_TEXT]
	},
	address: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_ADDRESS'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_ADDRESS', longName_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_ADDRESS', SPECIAL_CHARS_LITE_TEXT]
	},
	city: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_CITY'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_CITY', name_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_CITY', SPECIAL_CHARS_TEXT]
	},
	stateProvince: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PROVINCE'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_PROVINCE', longName_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_PROVINCE', SPECIAL_CHARS_TEXT]
	},
	usStateProvince: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PROVINCE']
	},
	postalCode: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_POSTAL_CODE'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_POSTAL_CODE', name_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_POSTAL_CODE', SPECIAL_CHARS_TEXT]
	},
	officePhone: {
		[`${Validators.required.name}`]: ['VALIDATION_MESSAGE_REQUIRED_PHONE'],
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_PHONE', name_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_PHONE', SPECIAL_CHARS_TEXT]
	},
	mobilePhone: {
		[`${Validators.maxLength.name.toLowerCase()}`]: ['VALIDATION_MESSAGE_MAXLENGTH_MOBILE', name_maxLength.toString()],
		[`${Validators.pattern.name}`]: ['VALIDATION_MESSAGE_SPECIALCHARS_MOBILE', SPECIAL_CHARS_TEXT]
	}
}

export interface IProfileControl {
	email: FormControl<string | null>;
	firstName: FormControl<string | null>;
	lastName: FormControl<string | null>;
	country: FormControl<UserProfileCountry | null>;
	roleInfo: FormControl<string | null>;
	practice: FormControl<PracticeInfo | null>;
	dateOfBirth: FormControl<DateOnly | null>;
	dateOfBirthFormat: FormControl<string | null>;
	distributor: FormControl<string | null>;
	hospital: FormControl<string | null>;
	company: FormControl<string | null>;
	address: FormControl<string | null>;
	city: FormControl<string | null>;
	stateProvince: FormControl<string | null>;
	usStateProvince: FormControl<string | null>;
	postalCode: FormControl<string | null>;
	officePhone: FormControl<string | null>;
	mobilePhone: FormControl<string | null>;
}

export interface ProfileForm {
	email: string;
	firstName: string;
	lastName: string;
	country: UserProfileCountry;
	roleInfo: string;
	practice: PracticeInfo | null;
	dateOfBirth: DateOnly;
	dateOfBirthFormat: string;
	distributor: string | null;
	hospital: string | null;
	company: string | null;
	address: string;
	city: string;
	stateProvince: string | null;
	usStateProvince: string | null;
	postalCode: string;
	officePhone: string;
	mobilePhone: string | null;
}
